import { useId } from "react";
import type { InputHTMLAttributes, TextareaHTMLAttributes } from "react";
import { INSTAGRAM_LIMITS } from "@unik/shared";

const FIELD_BASE =
  "w-full rounded-[var(--radius-control-sm)] bg-surface-sunken px-4 text-text-primary " +
  "border border-border-default " +
  "placeholder:text-text-tertiary " +
  "transition-colors duration-[var(--duration-fast)] " +
  "hover:border-border-strong " +
  "focus:border-accent focus:outline-none " +
  "disabled:opacity-40 disabled:cursor-not-allowed";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  hint?: string;
}

export function Input({ label, error, hint, id, className = "", ...rest }: InputProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const describedBy = error !== undefined ? `${fieldId}-error` : hint !== undefined ? `${fieldId}-hint` : undefined;

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={fieldId} className="type-label text-text-secondary">
        {label}
      </label>
      <input
        {...rest}
        id={fieldId}
        aria-invalid={error !== undefined}
        aria-describedby={describedBy}
        className={`${FIELD_BASE} h-11 ${error !== undefined ? "border-danger" : ""} ${className}`}
      />
      {error !== undefined && (
        <p id={`${fieldId}-error`} className="type-caption text-danger">
          {error}
        </p>
      )}
      {error === undefined && hint !== undefined && (
        <p id={`${fieldId}-hint`} className="type-caption text-text-tertiary">
          {hint}
        </p>
      )}
    </div>
  );
}

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  hint?: string;
  error?: string;
}

/** Textarea genérico (sem o contador de legenda do Instagram). */
export function TextArea({ label, hint, error, id, className = "", rows = 4, ...rest }: TextAreaProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={fieldId} className="type-label text-text-secondary">
        {label}
      </label>
      <textarea
        {...rest}
        id={fieldId}
        rows={rows}
        aria-invalid={error !== undefined}
        className={`${FIELD_BASE} resize-y py-3 leading-relaxed ${error !== undefined ? "border-danger" : ""} ${className}`}
      />
      {error !== undefined && (
        <p id={`${fieldId}-error`} className="type-caption text-danger">
          {error}
        </p>
      )}
      {error === undefined && hint !== undefined && <p className="type-caption text-text-tertiary">{hint}</p>}
    </div>
  );
}

interface CaptionFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  value: string;
  error?: string;
}

/**
 * Campo de legenda com contador.
 * O limite de 2.200 caracteres é do Instagram, não nosso (docs/11 §5.3) —
 * passar disso faz a publicação falhar, então é bloqueio, não aviso.
 */
export function CaptionField({
  label,
  value,
  error,
  id,
  className = "",
  ...rest
}: CaptionFieldProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const max = INSTAGRAM_LIMITS.captionMaxLength;
  const over = value.length > max;

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={fieldId} className="type-label text-text-secondary">
        {label}
      </label>
      <textarea
        {...rest}
        id={fieldId}
        value={value}
        rows={6}
        aria-invalid={over || error !== undefined}
        className={`${FIELD_BASE} resize-y py-3 leading-relaxed ${
          over || error !== undefined ? "border-danger" : ""
        } ${className}`}
      />
      <div className="flex items-start justify-between gap-4">
        <p className="type-caption text-danger">{error ?? ""}</p>
        <p
          className={`type-caption tabular-nums ${over ? "text-danger" : "text-text-tertiary"}`}
        >
          {value.length} / {max}
        </p>
      </div>
    </div>
  );
}
